import api from './api.service';
import type { ApiResponse } from '@/types';
import type { RejectionPayload } from './rejection.service';

export interface ProductionRequestLine {
  id: string;
  line_number: number;
  /** Set when the line uses an inventory finished good; otherwise null with manual fields. */
  item_id: string | null;
  boq_line_item_id?: string | null;
  quotation_line_number?: number | null;
  manual_product_code?: string | null;
  manual_product_description?: string | null;
  quantity: number;
  uom: string;
  target_production_date?: string | null;
  bom_id?: string | null;
  project_id?: string | null;
  stock_type: string;
  line_priority?: string | null;
  remarks?: string | null;
  quantity_released_to_wo: number;
  item?: { item_code?: string; item_name?: string } | null;
  project?: { id?: string; project_number?: string; project_name?: string };
  /** Loaded when GET includes line relations. */
  bom?: { id?: string; bom_number?: string; bom_name?: string } | null;
  /** BOQ estimation Material rows (+ inventory heat / MTC) when line has boq_line_item_id. */
  boq_materials?: Array<{
    id: string;
    description: string;
    heat_number?: string | null;
    mtc_document_number?: string | null;
    profile_grade?: string | null;
  }> | null;
}

export type PoWaiverReason = 'PO_will_follow_before_delivery' | 'Other';

export interface ProductionRequest {
  id: string;
  request_number: string;
  request_date: string;
  warehouse_id: string;
  department_id?: string | null;
  /** Optional inquiry / expected customer before PO. */
  customer_id?: string | null;
  customer?: { id?: string; customer_code?: string; customer_name?: string } | null;
  quotation_id?: string | null;
  quotation?: { id?: string; quotation_number?: string; quotation_name?: string; status?: string } | null;
  external_ref_no?: string | null;
  effective_date?: string | null;
  contact_person?: string | null;
  po_waiver_reason?: PoWaiverReason | string | null;
  po_waiver_reason_detail?: string | null;
  responsibility_notes?: string | null;
  signoff_estimation_user_id?: string | null;
  signoff_estimation_at?: string | null;
  signoff_operations_user_id?: string | null;
  signoff_operations_at?: string | null;
  signoffEstimationUser?: { first_name?: string; last_name?: string; username?: string } | null;
  signoffOperationsUser?: { first_name?: string; last_name?: string; username?: string } | null;
  status: string;
  priority: string;
  source_type: string;
  remarks?: string | null;
  approval_instance_id?: string | null;
  lines?: ProductionRequestLine[];
  /** Earlier PR of the same quotation family this request supplements (revision added lines). */
  parent_production_request_id?: string | null;
  parent_production_request?: { id: string; request_number: string; status: string } | null;
  /** List view only. */
  parent_request_number?: string | null;
  supplementary_requests?: Array<{ id: string; request_number: string; status: string }>;
  warehouse?: { warehouse_name?: string; id: string };
  /** Set on create from authenticated user; included on GET with relations. */
  requester?: { first_name?: string; last_name?: string; username?: string };
  created_at?: string;
}

const base = '/manufacturing/production-requests';

/** Suggested lines when linking a quotation (text from quotation/BOQ; no forced inventory link). */
export interface PreviewPrLineFromQuotation {
  quotation_line_number: number;
  item_id: string | null;
  boq_line_item_id: string | null;
  manual_product_code: string | null;
  manual_product_description: string | null;
  quantity: number;
  uom: string;
  stock_type: 'MTO_hybrid';
  bom_id: string | null;
  project_id: string | null;
  line_remarks: string | null;
  /** mode=remaining only */
  quote_quantity?: number;
  already_requested?: number;
  requested_in?: string[];
}

export interface PreviewPrFromQuotationResult {
  lines: PreviewPrLineFromQuotation[];
  skipped: number;
  customer_id: string | null;
  mode: 'full' | 'remaining';
  parent_production_request: { id: string; request_number: string } | null;
  unmatched_prior_lines: number;
}

/** mode 'remaining': only lines / qty not yet requested by other production requests of the quotation family. */
export async function previewProductionRequestLinesFromQuotation(
  quotationId: string,
  mode: 'full' | 'remaining' = 'full'
): Promise<PreviewPrFromQuotationResult> {
  const res = (await api.get<PreviewPrFromQuotationResult>(
    `${base}/preview-from-quotation/${quotationId}?mode=${mode}`
  )) as ApiResponse<PreviewPrFromQuotationResult>;
  if (res.success === false || res.data == null) {
    throw new Error(res.message || 'Could not load lines from quotation');
  }
  return res.data;
}

export async function listProductionRequests(params?: {
  status?: string;
  from_date?: string;
  to_date?: string;
}): Promise<ProductionRequest[]> {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.from_date) q.set('from_date', params.from_date);
  if (params?.to_date) q.set('to_date', params.to_date);
  const res = await api.get<ProductionRequest[]>(`${base}?${q.toString()}`);
  return res.data ?? [];
}

export interface QuotationProductionRequestUsage {
  quotation_id: string;
  family_has_active_pr: boolean;
  has_remaining: boolean;
  parent_request_number: string | null;
}

/**
 * Quotation usage by production requests (family-wide across revisions).
 * `quotation_ids`: cannot start a new request (fully requested) — hide unless editing that PR.
 * `usage`: quotations whose family already has a request; `has_remaining` means a supplementary request is possible.
 */
export async function getQuotationProductionRequestUsage(): Promise<{
  quotation_ids: string[];
  usage: QuotationProductionRequestUsage[];
}> {
  const res = await api.get<{ quotation_ids: string[]; usage: QuotationProductionRequestUsage[] }>(
    `${base}/used-quotation-ids`
  );
  const payload = res.data as { quotation_ids?: string[]; usage?: QuotationProductionRequestUsage[] } | undefined;
  return {
    quotation_ids: Array.isArray(payload?.quotation_ids) ? payload.quotation_ids : [],
    usage: Array.isArray(payload?.usage) ? payload.usage : [],
  };
}

export async function listUsedQuotationIdsForProductionRequests(): Promise<string[]> {
  return (await getQuotationProductionRequestUsage()).quotation_ids;
}

export async function getProductionRequest(id: string): Promise<ProductionRequest> {
  const res = await api.get<ProductionRequest>(`${base}/${id}`);
  if (!res.data) throw new Error('Production request not found');
  return res.data;
}

export async function createProductionRequest(body: Record<string, unknown>): Promise<ProductionRequest> {
  const res = (await api.post<ProductionRequest>(base, body)) as ApiResponse<ProductionRequest>;
  if (res.success === false || res.data == null) {
    throw new Error(res.message || 'Create failed');
  }
  return res.data;
}

export async function updateProductionRequest(
  id: string,
  body: Record<string, unknown>
): Promise<ProductionRequest> {
  const res = await api.put<ProductionRequest>(`${base}/${id}`, body);
  if (!res.data) throw new Error('Update failed');
  return res.data;
}

export async function submitProductionRequest(id: string): Promise<ProductionRequest> {
  const res = (await api.post<ProductionRequest>(`${base}/${id}/submit`)) as ApiResponse<ProductionRequest>;
  if (res.success === false || res.data == null) {
    throw new Error(res.message || 'Submit failed');
  }
  return res.data;
}

export async function approveProductionRequest(id: string, remarks?: string): Promise<ProductionRequest> {
  const res = (await api.post<ProductionRequest>(`${base}/${id}/approve`, { remarks })) as ApiResponse<ProductionRequest>;
  if (res.success === false || res.data == null) {
    throw new Error(res.message || 'Approve failed');
  }
  return res.data;
}

export async function rejectProductionRequest(id: string, payload: RejectionPayload): Promise<ProductionRequest> {
  const res = (await api.post<ProductionRequest>(`${base}/${id}/reject`, payload)) as ApiResponse<ProductionRequest>;
  if (res.success === false || res.data == null) {
    throw new Error(res.message || 'Reject failed');
  }
  return res.data;
}

export async function cancelProductionRequest(id: string): Promise<ProductionRequest> {
  const res = (await api.post<ProductionRequest>(`${base}/${id}/cancel`)) as ApiResponse<ProductionRequest>;
  if (res.success === false || res.data == null) {
    throw new Error(res.message || 'Cancel failed');
  }
  return res.data;
}

export async function signoffEstimationProductionRequest(id: string): Promise<ProductionRequest> {
  const res = (await api.post<ProductionRequest>(`${base}/${id}/signoff-estimation`)) as ApiResponse<ProductionRequest>;
  if (res.success === false || res.data == null) {
    throw new Error(res.message || 'Sign-off failed');
  }
  return res.data;
}

export async function signoffOperationsProductionRequest(id: string): Promise<ProductionRequest> {
  const res = (await api.post<ProductionRequest>(`${base}/${id}/signoff-operations`)) as ApiResponse<ProductionRequest>;
  if (res.success === false || res.data == null) {
    throw new Error(res.message || 'Sign-off failed');
  }
  return res.data;
}

export async function createWorkOrdersFromPr(
  id: string,
  allocations?: Array<{ production_request_line_id: string; quantity: number }>
): Promise<unknown[]> {
  const res = await api.post<unknown[]>(`${base}/${id}/create-work-orders`, { allocations });
  return res.data ?? [];
}
